import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useBranding } from '../lib/branding';
import { Button, Card, Field, GradientBar, Input, PasswordInput, Spinner } from '../components/ui';
import { DevQuickLogin } from '../components/DevBypass';

/**
 * Ingreso a un programa.
 *
 * Ya viene pintado con la marca de la institución: el BrandingProvider resuelve el
 * branding por el slug sin necesidad de sesión. Es lo primero que una institución
 * mira en una demo.
 */
export function TenantLogin() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { login } = useAuth();
  const branding = useBranding();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);

  if (branding.loading) return <Spinner label="Cargando…" />;

  if (branding.notFound) {
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <div className="text-center max-w-sm">
          <h1 className="text-xl font-semibold">Esta institución no existe</h1>
          <p className="text-fg-muted mt-2 text-[15px]">
            Revisá el enlace que te compartieron. Si el problema sigue, escribile a tu equipo de
            seguimiento.
          </p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const result = await login(slug ?? null, username.trim(), password);

      // Acá siempre se entra con el programa ya definido por la URL, así que la
      // API nunca devuelve opciones. Se contempla igual porque el tipo lo exige
      // y porque una pantalla en blanco sería peor que un mensaje.
      if (result.kind === 'choose') {
        setError('Tu cuenta participa en varias instituciones. Ingresá desde la pantalla principal');
        return;
      }

      navigate(result.user.mustChangePassword ? `/${slug}/cambiar-clave` : `/${slug}`, {
        replace: true,
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No pudimos iniciar tu sesión');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecovery = async (): Promise<void> => {
    setError(null);
    try {
      await api.post('/auth/forgot-password', { slug, email: username.trim() });
      setRecoverySent(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No pudimos enviar el correo');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <div className="grow flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            {branding.branding?.logoUrl ? (
              <img
                src={branding.branding.logoUrl}
                alt={branding.programName}
                className="h-12 mx-auto mb-4 object-contain"
              />
            ) : (
              <GradientBar className="w-16 mx-auto mb-5" />
            )}
            {/* Sin saludo propio se arma con el nombre de ahora, no con el que
                tenía el programa el día que se creó la pharma. */}
            <h1 className="text-2xl font-semibold tracking-tight">
              {branding.branding?.welcomeTitle || `Bienvenido a ${branding.programName}`}
            </h1>
            {branding.branding?.welcomeText && (
              <p className="text-fg-muted mt-2 text-[15px] leading-relaxed">
                {branding.branding.welcomeText}
              </p>
            )}
          </div>

          <Card className="p-6">
            {recovering ? (
              recoverySent ? (
                <div className="text-center py-2">
                  <p className="font-semibold">Revisá tu correo</p>
                  <p className="text-sm text-fg-muted mt-2">
                    Si ese correo está registrado, vas a recibir un mensaje con las instrucciones
                    para crear una contraseña nueva.
                  </p>
                  <Button
                    variant="ghost"
                    className="mt-5"
                    onClick={() => {
                      setRecovering(false);
                      setRecoverySent(false);
                    }}
                  >
                    Volver
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Field label="Tu correo" htmlFor="recovery-email">
                    <Input
                      id="recovery-email"
                      type="email"
                      autoComplete="email"
                      value={username}
                      onChange={(event) => setUsername(event.target.value)}
                    />
                  </Field>
                  {error && <p className="text-sm text-danger">{error}</p>}
                  <Button onClick={handleRecovery} fullWidth>
                    Enviar instrucciones
                  </Button>
                  <Button variant="ghost" fullWidth onClick={() => setRecovering(false)}>
                    Volver
                  </Button>
                </div>
              )
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <Field label="Usuario o correo" htmlFor="username">
                  <Input
                    id="username"
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    required
                  />
                </Field>

                <Field label="Contraseña" htmlFor="password">
                  <PasswordInput
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                </Field>

                {error && (
                  <p className="text-sm text-danger" role="alert">
                    {error}
                  </p>
                )}

                <Button type="submit" loading={submitting} fullWidth size="lg">
                  Ingresar
                </Button>

                <button
                  type="button"
                  onClick={() => setRecovering(true)}
                  className="w-full text-sm text-fg-muted hover:text-primary py-1"
                >
                  Olvidé mi contraseña
                </button>
              </form>
            )}
          </Card>

          <DevQuickLogin slug={slug ?? null} />

          {branding.branding?.supportPhone && (
            <p className="text-center text-sm text-fg-muted mt-6">
              ¿Necesitás ayuda? Llamá al{' '}
              <a
                href={`tel:${branding.branding.supportPhone}`}
                className="text-primary font-semibold"
              >
                {branding.branding.supportPhone}
              </a>
            </p>
          )}
        </div>
      </div>

      <footer className="text-center text-xs text-fg-subtle py-6 safe-bottom">
        {branding.programName} · funciona con Trellis
      </footer>
    </div>
  );
}
